// hooks/useSecurityNotifications.js
import { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import axios from 'axios';
import { UAParser } from 'ua-parser-js';

export const useSecurityNotifications = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const functions = getFunctions();

    // Obtener información del dispositivo
    const getDeviceInfo = () => {
        const parser = new UAParser();
        const result = parser.getResult();

        return {
            browser: `${result.browser.name} ${result.browser.version}`,
            os: `${result.os.name} ${result.os.version}`,
            device: result.device.type || 'Desktop',
            deviceModel: result.device.model || 'Unknown',
            userAgent: navigator.userAgent
        };
    };

    // Obtener información de ubicación e IP
    const getLocationInfo = async () => {
        try {
            // Obtener IP y ubicación usando ipapi.co (gratuito)
            const response = await axios.get('https://ipapi.co/json/', {
                timeout: 5000
            });

            return {
                ip: response.data.ip,
                country: response.data.country_name,
                region: response.data.region,
                city: response.data.city,
                timezone: response.data.timezone,
                isp: response.data.org,
                latitude: response.data.latitude,
                longitude: response.data.longitude
            };
        } catch (error) {
            console.warn('No se pudo obtener información de ubicación:', error);
            // Fallback básico
            return {
                ip: 'No disponible',
                country: 'No disponible',
                region: 'No disponible',
                city: 'No disponible',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                isp: 'No disponible',
                latitude: null,
                longitude: null
            };
        }
    };

    // Detectar si es un dispositivo/ubicación sospechosa
    const analyzeLoginRisk = (deviceInfo, locationInfo) => {
        let riskLevel = 'low';
        let riskFactors = [];

        // Análisis básico de riesgo
        if (locationInfo.ip === 'No disponible') {
            riskLevel = 'medium';
            riskFactors.push('IP no identificada');
        }

        if (deviceInfo.device === 'mobile' || deviceInfo.device === 'tablet') {
            riskFactors.push('Dispositivo móvil');
        }

        // Puedes agregar más lógica de análisis de riesgo aquí

        return { riskLevel, riskFactors };
    };

    // Función principal para enviar notificación de login
    const sendLoginNotification = async (userEmail, userName, loginMethod = 'email') => {
        console.log('🔐 Iniciando notificación de seguridad...');
        console.log('📧 Email destino:', userEmail);
        console.log('👤 Usuario:', userName);
        console.log('🔑 Método:', loginMethod);

        setLoading(true);
        setError(null);

        try {
            console.log('📱 Obteniendo información del dispositivo...');
            const deviceInfo = getDeviceInfo();
            console.log('📱 Device Info:', deviceInfo);

            console.log('🌍 Obteniendo información de ubicación...');
            const locationInfo = await getLocationInfo();
            console.log('🌍 Location Info:', locationInfo);

            const riskAnalysis = analyzeLoginRisk(deviceInfo, locationInfo);
            const timestamp = new Date();

            console.log('📤 Preparando email...');
            const sendEmail = httpsCallable(functions, 'sendEmail');

            // Crear el HTML del email
            const emailHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <style>
            .container { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; background: white; }
            .header { background: linear-gradient(135deg, #14840c, #16a34a); color: white; padding: 30px; text-align: center; border-radius: 12px 12px 0 0; }
            .content { padding: 30px; }
            .info-card { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 20px; margin: 15px 0; }
            .info-row { display: flex; justify-content: space-between; margin: 8px 0; padding: 8px 0; border-bottom: 1px solid #eee; }
            .info-label { font-weight: 600; color: #374151; }
            .info-value { color: #6b7280; }
            .risk-low { background: #d1fae5; border-left: 4px solid #10b981; padding: 15px; border-radius: 4px; }
            .risk-medium { background: #fef3c7; border-left: 4px solid #f59e0b; padding: 15px; border-radius: 4px; }
            .risk-high { background: #fee2e2; border-left: 4px solid #ef4444; padding: 15px; border-radius: 4px; }
            .footer { background: #f8f9fa; padding: 20px; text-align: center; border-radius: 0 0 12px 12px; border-top: 1px solid #e9ecef; }
            .map-link { display: inline-block; background: #14840c; color: white; padding: 8px 16px; border-radius: 6px; text-decoration: none; margin: 10px 0; }
            .security-tip { background: #e0f2fe; border: 1px solid #0288d1; border-radius: 8px; padding: 15px; margin: 20px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h1 style="margin: 0; font-size: 24px;">🔐 Inicio de Sesión Detectado</h1>
              <p style="margin: 10px 0 0 0; opacity: 0.9;">MiguelMX - Notificación de Seguridad</p>
            </div>
            
            <div class="content">
              <p><strong>Hola ${userName || 'Usuario'},</strong></p>
              <p>Hemos detectado un nuevo inicio de sesión en tu cuenta de MiguelMX.</p>
              
              <div class="info-card">
                <h3 style="margin-top: 0; color: #14840c;">📋 Detalles del Inicio de Sesión</h3>
                <div class="info-row">
                  <span class="info-label">Fecha y Hora:</span>
                  <span class="info-value">${timestamp.toLocaleString('es-ES', {
                timeZone: locationInfo.timezone,
                dateStyle: 'full',
                timeStyle: 'medium'
            })}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Método:</span>
                  <span class="info-value">${loginMethod === 'google' ? '🔍 Google Sign-In' : '📧 Email y Contraseña'}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Email:</span>
                  <span class="info-value">${userEmail}</span>
                </div>
              </div>

              <div class="info-card">
                <h3 style="margin-top: 0; color: #14840c;">🌍 Información de Ubicación</h3>
                <div class="info-row">
                  <span class="info-label">Dirección IP:</span>
                  <span class="info-value">${locationInfo.ip}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Ubicación:</span>
                  <span class="info-value">${locationInfo.city}, ${locationInfo.region}, ${locationInfo.country}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Zona Horaria:</span>
                  <span class="info-value">${locationInfo.timezone}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Proveedor:</span>
                  <span class="info-value">${locationInfo.isp}</span>
                </div>
                ${locationInfo.latitude && locationInfo.longitude ? `
                <div style="margin-top: 15px;">
                  <a href="https://maps.google.com/?q=${locationInfo.latitude},${locationInfo.longitude}" class="map-link" target="_blank">
                    🗺️ Ver en Google Maps
                  </a>
                </div>
                ` : ''}
              </div>

              <div class="info-card">
                <h3 style="margin-top: 0; color: #14840c;">💻 Información del Dispositivo</h3>
                <div class="info-row">
                  <span class="info-label">Navegador:</span>
                  <span class="info-value">${deviceInfo.browser}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Sistema Operativo:</span>
                  <span class="info-value">${deviceInfo.os}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Tipo de Dispositivo:</span>
                  <span class="info-value">${deviceInfo.device} ${deviceInfo.deviceModel !== 'Unknown' ? `(${deviceInfo.deviceModel})` : ''}</span>
                </div>
              </div>

              <div class="risk-${riskAnalysis.riskLevel}">
                <h4 style="margin-top: 0;">
                  ${riskAnalysis.riskLevel === 'low' ? '✅ Riesgo Bajo' : riskAnalysis.riskLevel === 'medium' ? '⚠️ Riesgo Medio' : '🚨 Riesgo Alto'}
                </h4>
                <p style="margin-bottom: 0;">
                  ${riskAnalysis.riskLevel === 'low'
                    ? 'Este inicio de sesión parece normal y seguro.'
                    : `Se detectaron algunos factores que requieren atención: ${riskAnalysis.riskFactors.join(', ')}`
                }
                </p>
              </div>

              <div class="security-tip">
                <h4 style="margin-top: 0;">💡 Consejos de Seguridad</h4>
                <ul style="margin-bottom: 0; padding-left: 20px;">
                  <li>Si no reconoces este inicio de sesión, cambia tu contraseña inmediatamente</li>
                  <li>Habilita la autenticación de dos factores para mayor seguridad</li>
                  <li>Revisa regularmente la actividad de tu cuenta</li>
                  <li>No compartas tus credenciales con terceros</li>
                </ul>
              </div>

              <p><strong>¿No fuiste tú?</strong></p>
              <p>Si no reconoces este inicio de sesión, te recomendamos:</p>
              <ol>
                <li>Cambiar tu contraseña inmediatamente</li>
                <li>Revisar tu actividad reciente en la cuenta</li>
                <li>Contactar a nuestro equipo de soporte: soporte@miguel.app</li>
              </ol>
            </div>

            <div class="footer">
              <p style="margin: 0; color: #6b7280; font-size: 14px;">
                <strong>MiguelMX Security Team</strong><br>
                Esta es una notificación automática de seguridad<br>
                Si tienes dudas, contacta: soporte@miguel.app
              </p>
              <p style="margin: 10px 0 0 0; color: #9ca3af; font-size: 12px;">
                © 2025 MiguelMX®. Todos los derechos reservados.
              </p>
            </div>
          </div>
        </body>
        </html>
      `;

            // Mensaje de texto alternativo
            const textMessage = `
Hola ${userName || 'Usuario'},

Se ha detectado un nuevo inicio de sesión en tu cuenta de MiguelMX:

📋 DETALLES:
- Fecha: ${timestamp.toLocaleString('es-ES')}
- Método: ${loginMethod === 'google' ? 'Google Sign-In' : 'Email y Contraseña'}
- Email: ${userEmail}

🌍 UBICACIÓN:
- IP: ${locationInfo.ip}
- Ubicación: ${locationInfo.city}, ${locationInfo.region}, ${locationInfo.country}
- Proveedor: ${locationInfo.isp}

💻 DISPOSITIVO:
- Navegador: ${deviceInfo.browser}
- Sistema: ${deviceInfo.os}
- Dispositivo: ${deviceInfo.device}

${riskAnalysis.riskLevel !== 'low' ? `⚠️ ATENCIÓN: ${riskAnalysis.riskFactors.join(', ')}` : '✅ Este inicio de sesión parece seguro.'}

Si no fuiste tú, cambia tu contraseña inmediatamente y contacta a soporte@miguel.app.

MiguelMX Security Team
      `;

            console.log('📧 Enviando email de seguridad...');
            console.log('📧 Datos del email:', {
                to: userEmail,
                subject: `🔐 Inicio de sesión detectado en MiguelMX - ${timestamp.toLocaleDateString('es-ES')}`,
                hasHtml: true,
                hasText: true
            });

            await sendEmail({
                to: userEmail,
                subject: `🔐 Inicio de sesión detectado en MiguelMX - ${timestamp.toLocaleDateString('es-ES')}`,
                message: textMessage.trim(),
                html: emailHTML
            });

            console.log('✅ Notificación de seguridad enviada correctamente');

            return {
                success: true,
                deviceInfo,
                locationInfo,
                riskAnalysis,
                timestamp
            };

        } catch (err) {
            const errorMessage = err.message || 'Error enviando notificación de seguridad';
            console.error('❌ Error en notificación de seguridad:', err);
            console.error('❌ Error completo:', {
                message: err.message,
                code: err.code,
                details: err.details
            });
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    return {
        sendLoginNotification,
        loading,
        error
    };
};

export default useSecurityNotifications;